import { useEffect, useState, useCallback } from "react";
import { useNavigate, Outlet, useMatch } from "react-router-dom";

// Components
import AccountSelector from "../components/AccountSelector";
import DateSelector from "../components/DateSelector";
import ReportCard from "../components/ReportCard";
import JsontoExcel from "../components/JsonToExcel";
import PendingItemsPanel from "../components/PendingItemsPanel";
import ReportErrorBanner from "../components/ReportErrorBanner";
import ExpiredConnectionsBanner from "../components/ExpiredConnectionsBanner";
import LoadingScreen from "../components/LoadingScreen";
import VerticalCompaniesTable from "../components/VerticalCompaniesTable";
import ReportFilters from "../components/ReportFilters";
import ReportPresetBar from "../components/ReportPresetBar";

// Utils & Config
import { transformMonthOverMonth } from "../utils/reportsHelpers";
import { useAppData } from "../context/AppDataContext";
import {
  assignIds,
  normalizeAmounts,
  buildNodeMap,
  recomputeDisplayAmount,
  pruneReportForExcel,
  condenseReportLineItems,
} from "../utils/reportTreeUtils";
import { CUSTOM_PERIOD, getDatesForPeriod } from "../utils/reportPeriods";
import { API_BASE_URL, fetchAuthenticated } from "../utils/config";

function Generate() {
  const navigate = useNavigate();

  // App-level data
  const {
    isSubscribed,
    companies: accounts,
    loading: appDataLoading,
  } = useAppData();

  /* =======================
     CORE PAGE STATE
     ======================= */
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportErrors, setReportErrors] = useState([]);

  /* =======================
     USER INPUT / UI STATE
     ======================= */
  const [showTypeError, setShowTypeError] = useState(false);
  const [reportType, setReportType] = useState("");
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [useAllAccounts, setUseAllAccounts] = useState(false);
  const [combine, setCombine] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [monthOverMonth, setMonthOverMonth] = useState(false);
  const [companiesVertical, setCompaniesVertical] = useState(false);
  const [reportPeriod, setReportPeriod] = useState("this_year_to_date");
  const [accountingMethod, setAccountingMethod] = useState("accrual");
  const [condenseLineItems, setCondenseLineItems] = useState(false);

  /* =======================
     SAVED REPORT PRESETS
     ======================= */
  const [presets, setPresets] = useState([]);
  const [presetsLoading, setPresetsLoading] = useState(true);
  const [selectedPresetId, setSelectedPresetId] = useState("");
  const [presetError, setPresetError] = useState("");
  const [presetNotice, setPresetNotice] = useState("");

  /* =======================
     REPORT PERIOD AUTO-DATES
     ======================= */
  useEffect(() => {
    // "custom" resolves to null, which leaves whatever is in the date inputs
    const dates = getDatesForPeriod(reportPeriod);
    if (dates) {
      setStartDate(dates.from);
      setEndDate(dates.to);
    }
  }, [reportPeriod]);

  /* =======================
     FETCHED DATA & TREE STATE
     ======================= */
  const [rawReportObject, setRawReportObject] = useState({});
  const [reportMonths, setReportMonths] = useState([]);
  const [originalReportData, setOriginalReportData] = useState([]);
  const [nodeMap, setNodeMap] = useState(new Map());
  const [removedIds, setRemovedIds] = useState(new Set());
  const [pendingRemoval, setPendingRemoval] = useState([]);
  const isViewingLineItem = useMatch("/generate/line-item");

  const [appliedSettings, setAppliedSettings] = useState({
    reportType: "",
    monthOverMonth: false,
    companiesVertical: false,
    combine: false,
  });

  /* =======================
     INITIALIZATION
     ======================= */
  useEffect(() => {
    if (appDataLoading) return;
    if (accounts && accounts.length > 0) {
      setSelectedAccounts((prev) =>
        prev.length ? prev : [accounts[0].accountId]
      );
    }
    setLoading(false);
  }, [accounts, appDataLoading]);

  /* =======================
     PRESET LOADING & ACTIONS
     ======================= */
  useEffect(() => {
    let cancelled = false;

    const loadPresets = async () => {
      try {
        const res = await fetchAuthenticated(
          `${API_BASE_URL}/reportPresets/getPresets`
        );
        const body = res ? await res.json() : null;
        if (!cancelled) setPresets(body?.data ?? []);
      } catch (err) {
        console.error("Error loading report presets:", err);
        if (!cancelled) setPresetError("Could not load your saved reports.");
      } finally {
        if (!cancelled) setPresetsLoading(false);
      }
    };

    loadPresets();
    return () => {
      cancelled = true;
    };
  }, []);

  const applyPreset = (preset) => {
    setPresetError("");
    setSelectedPresetId(preset.id);

    setReportType(preset.reportType || "");
    setShowTypeError(false);
    setUseAllAccounts(preset.useAllAccounts);
    setCombine(preset.combined);
    setMonthOverMonth(preset.monthOverMonth);
    setCompaniesVertical(preset.companiesVertical);
    setAccountingMethod(preset.accountingMethod || "accrual");

    // Companies can be disconnected after a preset is saved, so only restore
    // the ones still connected and say so rather than failing at report time
    const connectedIds = new Set((accounts || []).map((a) => a.accountId));
    const stillConnected = (preset.accountIds || []).filter((id) =>
      connectedIds.has(id)
    );
    const droppedCount = (preset.accountIds || []).length - stillConnected.length;
    setSelectedAccounts(stillConnected);

    const period = preset.reportPeriod || CUSTOM_PERIOD;
    setReportPeriod(period);
    if (period === CUSTOM_PERIOD) {
      setStartDate(preset.startDate || "");
      setEndDate(preset.endDate || "");
    }

    setPresetNotice(
      droppedCount > 0
        ? `Applied "${preset.name}". ${droppedCount} saved ${
            droppedCount === 1 ? "company is" : "companies are"
          } no longer connected and ${
            droppedCount === 1 ? "was" : "were"
          } skipped.`
        : `Applied "${preset.name}".`
    );
  };

  const savePreset = async (name) => {
    setPresetError("");
    setPresetNotice("");

    if (!reportType) {
      setPresetError("Pick a report type before saving a setup.");
      return;
    }

    try {
      const res = await fetchAuthenticated(
        `${API_BASE_URL}/reportPresets/savePreset`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name,
            reportType,
            accountIds: useAllAccounts ? [] : selectedAccounts,
            useAllAccounts,
            combined: combine,
            monthOverMonth: reportType === "TrialBalance" ? false : monthOverMonth,
            companiesVertical:
              reportType === "TrialBalance" ? false : companiesVertical,
            reportPeriod,
            startDate,
            endDate,
            accountingMethod,
          }),
        }
      );

      const saved = (await res.json())?.data;
      // Saving under an existing name overwrites it, so replace in place
      setPresets((prev) => [saved, ...prev.filter((p) => p.id !== saved.id)]);
      setSelectedPresetId(saved.id);
      setPresetNotice(`Saved "${saved.name}".`);
    } catch (err) {
      console.error("Error saving report preset:", err);
      setPresetError(err?.data?.error || "Could not save this setup.");
    }
  };

  const deletePreset = async (preset) => {
    setPresetError("");
    setPresetNotice("");

    try {
      await fetchAuthenticated(`${API_BASE_URL}/reportPresets/deletePreset`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ presetId: preset.id }),
      });

      setPresets((prev) => prev.filter((p) => p.id !== preset.id));
      setSelectedPresetId("");
      setPresetNotice(`Deleted "${preset.name}".`);
    } catch (err) {
      console.error("Error deleting report preset:", err);
      setPresetError(err?.data?.error || "Could not delete this saved report.");
    }
  };

  const handleReportTypeChange = (val) => {
    setReportType(val);
    if (val) setShowTypeError(false);

    if (val === "TrialBalance") {
      setMonthOverMonth(false);
      setCompaniesVertical(false);
    }
  };

  // Handle Vertical Mode logic
  const handleVerticalToggle = (val) => {
    setCompaniesVertical(val);
    if (val) {
      setMonthOverMonth(false);
      setCombine(false);
    }
  };

  /* =======================
     TREE VISIBILITY LOGIC
     ======================= */
  const buildVisibleTree = useCallback(
    (node) => {
      if (removedIds.has(node.id)) return null;

      const visibleChildren = (node.children || [])
        .map(buildVisibleTree)
        .filter(Boolean);

      const displayAmount =
        removedIds.size === 0
          ? node.rawAmount
          : recomputeDisplayAmount(node, visibleChildren);

      return { ...node, children: visibleChildren, displayAmount };
    },
    [removedIds]
  );

  const visibleReportData = originalReportData.map(buildVisibleTree).filter(Boolean);

  const displayReportData =
    condenseLineItems && !appliedSettings.companiesVertical
      ? condenseReportLineItems(visibleReportData)
      : visibleReportData;

  /* =======================
     REPORT FETCH
     ======================= */
  const fetchReport = useCallback(async () => {
    if (!isSubscribed) {
      const goToProfile = window.confirm(
        "You need an active subscription to generate reports.\n\nGo to your profile to subscribe?"
      );
      if (goToProfile) navigate("/profile");
      return;
    }

    if (!reportType) {
      setShowTypeError(true);
      return;
    }

    setShowTypeError(false);
    setReportLoading(true);

    // Reset tree/error states
    setRemovedIds(new Set());
    setPendingRemoval([]);
    setReportMonths([]);
    setRawReportObject({});
    setReportErrors([]);
    setCondenseLineItems(false);

    try {
      const isTrialBalance = reportType === "TrialBalance";

      const body = {
        accountIds: useAllAccounts ? [] : selectedAccounts,
        combined: combine && (useAllAccounts || selectedAccounts.length > 1),
        monthOverMonth: isTrialBalance ? false : monthOverMonth,
        companiesVertical: isTrialBalance ? false : companiesVertical,
        accountingMethod,
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
      };

      const res = await fetchAuthenticated(
        `${API_BASE_URL}/quickbooks/get${reportType}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );

      const result = await res.json();
      const dataToProcess = result?.data ?? result;

      if (dataToProcess && dataToProcess.errors) {
        setReportErrors(dataToProcess.errors);
      }

      setRawReportObject(dataToProcess);

      let processed = [];
      if (monthOverMonth) {
        const combinedObj =
          dataToProcess.combinedReports ?? result.combinedReports;

        if (combinedObj) {
          processed = transformMonthOverMonth(combinedObj);
          setReportMonths(Object.keys(combinedObj).sort());
        } else if (dataToProcess?.companyReports) {
          const companies = Object.entries(dataToProcess.companyReports);
          processed = companies.map(([name, reports]) => ({
            name,
            children: transformMonthOverMonth(reports),
            months: Object.keys(reports).sort(),
          }));
          setReportMonths(Object.keys(companies[0]?.[1] || {}).sort());
        }
      } else {
        processed = Array.isArray(dataToProcess) ? dataToProcess : [];
      }

      const normalized = assignIds(processed).map(normalizeAmounts);
      setOriginalReportData(normalized);
      setNodeMap(buildNodeMap(normalized));

      setAppliedSettings({
        reportType,
        monthOverMonth: isTrialBalance ? false : monthOverMonth,
        companiesVertical: isTrialBalance ? false : companiesVertical,
        combine,
      });
    } catch (err) {
      console.error("Error generating report:", err);
      if (err?.data?.error === "quickbooks_connection_expired") {
        setReportErrors([
          `${err.data.company}: QuickBooks connection expired — reconnect it on the Companies page`,
        ]);
      } else if (err instanceof Error) {
        setReportErrors(["Report generation failed. Please try again."]);
      }
    } finally {
      setReportLoading(false);
    }
  }, [
    isSubscribed,
    reportType,
    selectedAccounts,
    useAllAccounts,
    combine,
    startDate,
    endDate,
    monthOverMonth,
    companiesVertical,
    accountingMethod,
    navigate,
  ]);

  const hideNode = (id) => {
    const node = nodeMap.get(id);
    if (!node) return;

    // 1. Mark as hidden so it disappears from the report
    setRemovedIds((prev) => new Set([...prev, id]));

    // 2. Simply add to the pending list (no more groups/balancing logic)
    setPendingRemoval((prev) => [...prev, id]);
  };

  const restoreAll = () => {
    setRemovedIds(new Set());
    setPendingRemoval([]);
  };

  const restoreNode = (nodeId) => {
    setRemovedIds((prev) => {
      const next = new Set(prev);
      next.delete(nodeId);
      return next;
    });

    setPendingRemoval((p) => p.filter((id) => id !== nodeId));

  };

  const excelReportData =
    appliedSettings.monthOverMonth
      ? rawReportObject
      : removedIds.size === 0
        ? rawReportObject
        : Array.isArray(originalReportData)
          ? originalReportData
              .map((n) => pruneReportForExcel(n, removedIds))
              .filter(Boolean)
          : rawReportObject;

  // Companies whose QuickBooks connection has expired (see backend `active` flag)
  const expiredAccounts = (accounts || []).filter((acc) => acc.active === false);

  // export button helper
  const exportButton =
    !reportLoading && visibleReportData.length > 0 ? (
      <JsontoExcel
        rawReportObject={excelReportData}
        visibleReportData={displayReportData}
        removedIds={removedIds}
        reportMeta={{
          reportType: appliedSettings.reportType,
          startDate,
          endDate,
          monthOverMonth: appliedSettings.monthOverMonth,
          combine: appliedSettings.combine,
          vertical: appliedSettings.companiesVertical,
        }}
      />
    ) : null;

  /* =======================
     RENDER
     ======================= */
  if (loading || appDataLoading) {
    return <LoadingScreen title="Preparing report tools…" />;
  }

  return (
    <div className="page page--generate">
      {!isViewingLineItem && (
        <>
          <h1 className="page-title">Generate Reports</h1>

          {accounts?.length === 0 ? (
            <div className="card" style={{ textAlign: "center", padding: "40px" }}>
              <h2>No Companies Connected</h2>
              <p className="muted">Connect a QuickBooks company to begin.</p>
              <button
                className="primary-btn"
                onClick={() => navigate("/companies")}
                style={{ marginTop: "20px" }}
              >
                Connect a Company
              </button>
            </div>
          ) : (
            <>
              <ExpiredConnectionsBanner
                companies={expiredAccounts}
                actionLabel="Reconnect on Companies page"
                onAction={() => navigate("/companies")}
              />

              <ReportPresetBar
                presets={presets}
                loading={presetsLoading}
                selectedPresetId={selectedPresetId}
                onApply={applyPreset}
                onSave={savePreset}
                onDelete={deletePreset}
                error={presetError}
                notice={presetNotice}
              />

              <ReportFilters
                reportPeriod={reportPeriod}
                setReportPeriod={setReportPeriod}
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                accountingMethod={accountingMethod}
                setAccountingMethod={setAccountingMethod}
              />

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  fetchReport();
                }}
              >
                <div className="card-grid">
                  <div className="card-column">
                    <div className="card">
                      <AccountSelector
                        accounts={accounts}
                        useAllAccounts={useAllAccounts}
                        selectedAccounts={selectedAccounts}
                        onToggleAll={() => setUseAllAccounts((p) => !p)}
                        onSelect={(id) =>
                          setSelectedAccounts((p) =>
                            p.includes(id)
                              ? p.filter((x) => x !== id)
                              : [...p, id]
                          )
                        }
                      />
                    </div>
                  </div>

                  <div className="card">
                    <DateSelector
                      reportType={reportType}
                      setReportType={handleReportTypeChange}
                      combine={combine}
                      setCombine={setCombine}
                      multi={useAllAccounts || selectedAccounts.length > 1}
                      monthOverMonth={monthOverMonth}
                      setMonthOverMonth={setMonthOverMonth}
                      companiesVertical={companiesVertical}
                      setCompaniesVertical={handleVerticalToggle}
                    />

                    {showTypeError && (
                      <div className="error-message-container">
                        <p className="error-banner-text">
                          ⚠️ Please select a report type
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </form>

              <div className="reports-container">
                {reportLoading && (
                  <LoadingScreen title="Generating report…" delayMs={8000} />
                )}

                <ReportErrorBanner errors={reportErrors} />

                {!reportLoading && visibleReportData.length > 0 && (
                  appliedSettings.companiesVertical &&
                  !appliedSettings.monthOverMonth ? (
                    <div className="card">
                      <div className="horizontal-scroll">
                        <div className="report-title-row">
                          <h1>{appliedSettings.reportType}</h1>

                          <div className="report-export-wrapper">
                            {!appliedSettings.companiesVertical && (
                              <button
                                type="button"
                                className={`export-btn group-btn-primary ${condenseLineItems ? "active" : ""}`}
                                onClick={() => setCondenseLineItems((prev) => !prev)}
                              >
                                {condenseLineItems ? "Show Details" : "Condense Line Items"}
                              </button>
                            )}

                            {exportButton}
                          </div>
                        </div>
                        <VerticalCompaniesTable data={displayReportData} />
                      </div>
                    </div>
                  ) : (
                    <div className="card">
                      <div className="horizontal-scroll">
                        <div className="report-title-row">
                          <h1>{appliedSettings.reportType}</h1>

                          <div className="report-export-wrapper">
                            {!appliedSettings.companiesVertical && (
                              <button
                                type="button"
                                className={`export-btn group-btn-primary ${condenseLineItems ? "active" : ""}`}
                                onClick={() => setCondenseLineItems((prev) => !prev)}
                              >
                                {condenseLineItems ? "Show Details" : "Condense Line Items"}
                              </button>
                            )}

                            {exportButton}
                          </div>
                        </div>
                        <ReportCard
                          title=""
                          data={displayReportData}
                          months={appliedSettings.monthOverMonth ? reportMonths : []}
                          onHide={hideNode}
                          isTrialBalance={appliedSettings.reportType === "TrialBalance"}
                        />
                      </div>
                    </div>
                  )
                )}

                {!appliedSettings.monthOverMonth && (
                  <>
                    <PendingItemsPanel
                      pendingIds={pendingRemoval}
                      nodeMap={nodeMap}
                      onRestore={restoreNode}
                      onRestoreAll={restoreAll}
                    />
                  </>
                )}
              </div>
            </>
          )}
        </>
      )}
      <Outlet />
    </div>
  );
}

export default Generate;